import { ChevronLeft, ChevronRight, Minus, PanelLeft, Plus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { cn } from '../../../lib/utils';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface PdfViewerProps {
	url: string;
	onMetadata?: (meta: { numPages: number }) => void;
}

export function PdfViewer({ url, onMetadata }: PdfViewerProps) {
	const [numPages, setNumPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [zoomIdx, setZoomIdx] = useState(2);
	const zoom = ZOOM_STEPS[zoomIdx];
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);
	const thumbRefs = useRef<Record<number, HTMLButtonElement | null>>({});

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setContainerWidth(entry.contentRect.width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const hasPrev = pageNumber > 1;
	const hasNext = pageNumber < numPages;

	const goTo = useCallback(
		(page: number) => {
			if (page >= 1 && page <= numPages) setPageNumber(page);
		},
		[numPages]
	);

	useEffect(() => {
		if (sidebarOpen && thumbRefs.current[pageNumber]) {
			thumbRefs.current[pageNumber]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}
	}, [pageNumber, sidebarOpen]);

	return (
		<div className="flex h-full flex-col">
			{/* Toolbar */}
			<div className="flex shrink-0 items-center border-b border-line-1 bg-surface px-4 py-2">
				<button
					type="button"
					onClick={() => setSidebarOpen(!sidebarOpen)}
					className={cn(
						'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
						sidebarOpen
							? 'bg-surface-input text-text-1'
							: 'text-text-3 hover:bg-surface-hover hover:text-text-1'
					)}
				>
					<PanelLeft size={14} />
				</button>
				<div className="flex flex-1 items-center justify-center gap-2">
					<button
						type="button"
						onClick={() => goTo(pageNumber - 1)}
						disabled={!hasPrev}
						className={cn(
							'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
							hasPrev
								? 'text-text-3 hover:bg-surface-hover hover:text-text-1'
								: 'text-text-4/30'
						)}
					>
						<ChevronLeft size={14} />
					</button>
					<span className="min-w-15 text-center text-sm text-text-2">
						{pageNumber} / {numPages || '—'}
					</span>
					<button
						type="button"
						onClick={() => goTo(pageNumber + 1)}
						disabled={!hasNext}
						className={cn(
							'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
							hasNext
								? 'text-text-3 hover:bg-surface-hover hover:text-text-1'
								: 'text-text-4/30'
						)}
					>
						<ChevronRight size={14} />
					</button>
					<div className="mx-2 h-4 w-px bg-line-1" />
					<button
						type="button"
						onClick={() => setZoomIdx(Math.max(0, zoomIdx - 1))}
						disabled={zoomIdx === 0}
						className={cn(
							'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
							zoomIdx > 0
								? 'text-text-3 hover:bg-surface-hover hover:text-text-1'
								: 'text-text-4/30'
						)}
					>
						<Minus size={12} />
					</button>
					<span className="min-w-10 text-center font-mono text-xs text-text-3">
						{Math.round(zoom * 100)}%
					</span>
					<button
						type="button"
						onClick={() => setZoomIdx(Math.min(ZOOM_STEPS.length - 1, zoomIdx + 1))}
						disabled={zoomIdx === ZOOM_STEPS.length - 1}
						className={cn(
							'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
							zoomIdx < ZOOM_STEPS.length - 1
								? 'text-text-3 hover:bg-surface-hover hover:text-text-1'
								: 'text-text-4/30'
						)}
					>
						<Plus size={12} />
					</button>
				</div>
				<div className="w-6" />
			</div>

			{/* Content */}
			<div className="flex min-h-0 flex-1">
				{/* Sidebar */}
				<div
					className={cn(
						'scroll-thin shrink-0 overflow-y-auto border-r border-line-1 bg-surface-sunken transition-[width] duration-200',
						sidebarOpen ? 'w-[160px]' : 'w-0'
					)}
				>
					{sidebarOpen && numPages > 0 && (
						<Document file={url} className="flex flex-col items-center gap-3 p-3">
							{Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
								<button
									key={p}
									ref={(el) => {
										thumbRefs.current[p] = el;
									}}
									type="button"
									onClick={() => setPageNumber(p)}
									className={cn(
										'group flex w-full flex-col items-center gap-1.5 rounded-lg p-1.5 transition-colors',
										p === pageNumber
											? 'bg-surface-fill'
											: 'hover:bg-surface-hover'
									)}
								>
									<div
										className={cn(
											'overflow-hidden rounded-md border transition-colors',
											p === pageNumber
												? 'border-accent ring-1 ring-accent/30'
												: 'border-line-1'
										)}
									>
										<Page
											pageNumber={p}
											width={108}
											renderTextLayer={false}
											renderAnnotationLayer={false}
											loading={
												<div className="flex h-[100px] w-[140px] items-center justify-center bg-surface text-2xs text-text-4">
													…
												</div>
											}
										/>
									</div>
									<span
										className={cn(
											'text-2xs',
											p === pageNumber
												? 'font-medium text-text-1'
												: 'text-text-4'
										)}
									>
										{p}
									</span>
								</button>
							))}
						</Document>
					)}
				</div>

				{/* Main page */}
				<div
					ref={containerRef}
					className="scroll-thin min-h-0 min-w-0 flex-1 overflow-auto bg-canvas"
				>
					<Document
						file={url}
						onLoadSuccess={({ numPages: n }) => {
							setNumPages(n);
							onMetadata?.({ numPages: n });
						}}
						loading={
							<div className="flex h-full items-center justify-center text-base text-text-4">
								Loading PDF…
							</div>
						}
						error={
							<div className="flex h-full items-center justify-center text-base text-text-4">
								Failed to load PDF
							</div>
						}
						className="flex flex-col items-center gap-4 p-6"
					>
						{containerWidth > 0 && (
							<Page
								pageNumber={pageNumber}
								width={Math.min(containerWidth - 48, 800) * zoom}
								className="shadow-lg"
								renderTextLayer={false}
								renderAnnotationLayer={false}
								loading={
									<div className="flex h-[600px] w-[450px] items-center justify-center rounded-md bg-surface text-sm text-text-4">
										Loading page…
									</div>
								}
							/>
						)}
					</Document>
				</div>
			</div>
		</div>
	);
}
